/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ForwardingsTab.tsx
 🗓️ Created: 2026-01-04
 🧩 Description:
    Forwardings tab for Extension form - Configure call forwarding settings
    (Call Forward, On Busy, No Answer, Not Registered, Do Not Disturb)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect } from 'react';
import { Phone } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import SelectTimeGroup from '../../../components/ui/SelectTimeGroup';
import { fetchTimeGroups } from '../../timeGroups/api/timeGroupApi';
import type { FollowMeSettingsState } from '../types';

interface TimeGroupOption {
    id: number;
    name: string;
    description?: string;
}

interface ForwardingsTabProps {
    followMeData: FollowMeSettingsState;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFollowMeChange: (field: string, value: any) => void;
}

export function ForwardingsTab({
    followMeData,
    onFollowMeChange,
}: ForwardingsTabProps) {
    const { t } = useI18n();
    const [timeGroups, setTimeGroups] = useState<TimeGroupOption[]>([]);
    const [loadingTimeGroups, setLoadingTimeGroups] = useState(false);

    // Load time groups
    useEffect(() => {
        const loadTimeGroups = async () => {
            setLoadingTimeGroups(true);
            try {
                const result = await fetchTimeGroups({
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                });
                setTimeGroups(Array.isArray(result.data) ? result.data : []);
            } catch (error) {
                console.error('Failed to load time groups:', error);
            } finally {
                setLoadingTimeGroups(false);
            }
        };
        loadTimeGroups();
    }, []);

    const renderToggleField = (
        field: string,
        label: string,
        enabled: boolean,
        onToggle: (enabled: boolean) => void,
        destination?: string,
        onDestinationChange?: (value: string) => void,
        tooltip?: string,
        timeGroupId?: number | null,
        onTimeGroupChange?: (value: number | null) => void
    ) => {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 items-start">
                <SimpleTooltip content={tooltip || ''} position="top" maxWidth="300px">
                    <Label className="text-left font-medium">{label}</Label>
                </SimpleTooltip>
                <Toggle
                    value={enabled}
                    onChange={onToggle}
                />
                {timeGroupId !== undefined && onTimeGroupChange ? (
                    <SelectTimeGroup
                        value={timeGroupId ?? null}
                        onChange={(value) => onTimeGroupChange(value)}
                        timeGroups={timeGroups}
                        placeholder={loadingTimeGroups ? t('common.loading') : t('extensions.forwardings.timeGroup.placeholder', 'Select Time Group (Optional)')}
                        disabled={loadingTimeGroups || !enabled}
                    />
                ) : (
                    <div></div>
                )}
                {destination !== undefined && onDestinationChange ? (
                    <Input
                        value={destination}
                        onChange={(e) => onDestinationChange(e.target.value)}
                        placeholder={t('extensions.forwardings.destinationPlaceholder', 'Destination')}
                        disabled={!enabled}
                        className={!enabled ? 'opacity-50' : ''}
                    />
                ) : (
                    <div></div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Call Forwarding Section */}
            <FormBox
                title={t('extensions.forwardings.title', 'Call Forwarding')}
                icon={<Phone className="h-5 w-5" />}
            >
                <div className="space-y-2">
                    {/* Call Forward */}
                    {renderToggleField(
                        'callForward',
                        t('extensions.forwardings.callForward.label', 'Call Forward'),
                        followMeData.callForwardEnabled,
                        (enabled) => onFollowMeChange('callForwardEnabled', enabled),
                        followMeData.callForwardDestination,
                        (value) => onFollowMeChange('callForwardDestination', value),
                        t('extensions.forwardings.callForward.tooltip', 'Forward all calls to the specified destination.'),
                        followMeData.callForwardTimeGroupId,
                        (value) => onFollowMeChange('callForwardTimeGroupId', value)
                    )}

                    {/* On Busy */}
                    {renderToggleField(
                        'onBusy',
                        t('extensions.forwardings.onBusy.label', 'On Busy'),
                        followMeData.onBusyEnabled,
                        (enabled) => onFollowMeChange('onBusyEnabled', enabled),
                        followMeData.onBusyDestination,
                        (value) => onFollowMeChange('onBusyDestination', value),
                        t('extensions.forwardings.onBusy.tooltip', 'If enabled, it overrides the value of voicemail enabling in extension.'),
                        followMeData.onBusyTimeGroupId,
                        (value) => onFollowMeChange('onBusyTimeGroupId', value)
                    )}

                    {/* No Answer */}
                    {renderToggleField(
                        'noAnswer',
                        t('extensions.forwardings.noAnswer.label', 'No Answer'),
                        followMeData.noAnswerEnabled,
                        (enabled) => onFollowMeChange('noAnswerEnabled', enabled),
                        followMeData.noAnswerDestination,
                        (value) => onFollowMeChange('noAnswerDestination', value),
                        t('extensions.forwardings.noAnswer.tooltip', 'If enabled, it overrides the value of voicemail enabling in extension.'),
                        followMeData.noAnswerTimeGroupId,
                        (value) => onFollowMeChange('noAnswerTimeGroupId', value)
                    )}

                    {/* Not Registered */}
                    {renderToggleField(
                        'notRegistered',
                        t('extensions.forwardings.notRegistered.label', 'Not Registered'),
                        followMeData.notRegisteredEnabled,
                        (enabled) => onFollowMeChange('notRegisteredEnabled', enabled),
                        followMeData.notRegisteredDestination,
                        (value) => onFollowMeChange('notRegisteredDestination', value),
                        t('extensions.forwardings.notRegistered.tooltip', 'If endpoint is not reachable, forward to this destination before going to voicemail.'),
                        followMeData.notRegisteredTimeGroupId,
                        (value) => onFollowMeChange('notRegisteredTimeGroupId', value)
                    )}

                    {/* Do Not Disturb */}
                    {renderToggleField(
                        'doNotDisturb',
                        t('extensions.forwardings.doNotDisturb.label', 'Do Not Disturb'),
                        followMeData.doNotDisturb,
                        (enabled) => onFollowMeChange('doNotDisturb', enabled),
                        undefined,
                        undefined,
                        t('extensions.forwardings.doNotDisturb.tooltip', 'Enable Do Not Disturb mode for this extension.'),
                        followMeData.doNotDisturbTimeGroupId,
                        (value) => onFollowMeChange('doNotDisturbTimeGroupId', value)
                    )}
                </div>
            </FormBox>
        </div>
    );
}
